import { HttpClient, HttpErrorResponse, HttpEvent, HttpResponse } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { FeatureName } from "@common/ADAPT.Common.Model/embed/feature-name.enum";
import { ChatAudit, ChatAuditBreezeModel, RequestType } from "@common/ADAPT.Common.Model/organisation/chat-audit";
import { Organisation } from "@common/ADAPT.Common.Model/organisation/organisation";
import { ServiceUri } from "@common/configuration/service-uri";
import { IdentityStorageService } from "@common/identity/identity-storage.service";
import { LocalStorage } from "@common/lib/storage/local-storage";
import { ShellUiService } from "@common/shell/shell-ui.service";
import { UserService } from "@common/user/user.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { EditSimpleValueBreezeEntityDialogComponent, IEditSimpleEntity, IEditSimpleValueBreezeEntityDialogData, SimpleValueType } from "@common/ux/edit-simple-value-breeze-entity-dialog/edit-simple-value-breeze-entity-dialog.component";
import { ResponsiveService } from "@common/ux/responsive/responsive.service";
import DataSource from "devextreme/data/data_source";
import { Alert, Message } from "devextreme/ui/chat";
import { ResponseOutputItem } from "openai/resources/responses/responses.mjs";
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, filter, map, merge, Observable, of, shareReplay, startWith, Subject, switchMap } from "rxjs";
import { CommonDataService } from "../data/common-data.service";
import { MethodologyPredicate } from "../data/methodology-predicate";
import { ArrayUtilities } from "../utilities/array-utilities";
import { IChatResponseBindingModel } from "./chat-ai.service";
import { CHAT_AI_ORG_PROVIDER, IChatAiOrgProvider, IMegaPromptLabel } from "./chat-ai-org-provider.interface";
import { ChatResponseAbortedError } from "./chat-response-aborted-error";
import { MegaPromptName } from "./mega-prompts";

export const ChatAiSidebarTabId = "chat-ai-sidebar-tab";

export enum ProgressType {
    Thinking = "thinking",
    FunctionCall = "functionCall",
}

export type ChatMessage = Message & { renderedMessage?: string, completed: boolean, progressType?: ProgressType };

export interface IChat {
    conversationId?: string;
    previousResponseId?: string;
    output: ResponseOutputItem[];

    store: ChatMessage[];
    dataSource: DataSource<ChatMessage, string>;
    isDisabled: boolean;
    alerts: Alert[];
    alertsSubject: BehaviorSubject<Alert[]>;
}

export interface IChatHistoryEntry {
    userMessage: string;
    assistantMessage: string;
    timestamp: Date;
    userMessageTimestamp: Date;
}

export interface IPersistChat {
    chat: IChat;
    totalCost: number;
}

export enum ChatLocationMode {
    ApplicationBarDialog,
    Sidebar,
}

const ChatLocationModeStorageKey = "ChatLocationMode";
const StreamTimeoutMs = 10 * 60 * 1000;

@Injectable({
    providedIn: "root",
})
export class ChatAiUiService {
    public featureEnabled$: Observable<boolean>;
    public applicableMegaPromptLabels$: Observable<IMegaPromptLabel[]>;
    private cachedMegaPromptNames$?: Observable<MegaPromptName[]>;

    private persistedChat?: IPersistChat;
    private chatLocationModeSubject: BehaviorSubject<ChatLocationMode>;
    private switchingLocationMode = false;
    private chatVisibleChangeEvent$ = new Subject<void>();

    // organisation changed event will be emitted to refresh sidebar - so this one here is will only emit if the organisation is actually changed.
    private orgChangedEvent$: Observable<Organisation | undefined>;

    private persistedChatClearedEvent$ = new Subject<void>();

    private queueExecutePromptName$ = new BehaviorSubject<IMegaPromptLabel | undefined>(undefined);
    private responseStreamingSubject = new BehaviorSubject(false);
    private responseAbortController?: AbortController;
    private activeResponsePromise?: Promise<void>;
    // Aborting a stream does not guarantee its parser has stopped before the three-second cleanup wait expires.
    // Keep this generation in the root service so destroyed and replacement components share the same value;
    // callbacks from an older stream can then detect that the user created, selected, or cleared a chat and avoid
    // applying or persisting their delayed output into the newly active chat.
    private chatGeneration = 0;

    public hasChatAiSidebarTab = false;

    public constructor(
        @Inject(CHAT_AI_ORG_PROVIDER) private chatAiOrgProvider: IChatAiOrgProvider,
        private responsiveService: ResponsiveService,
        private shellUiService: ShellUiService,
        private httpClient: HttpClient,
        private identityStorageService: IdentityStorageService,
        private commonDataService: CommonDataService,
        private userService: UserService,
        private dialogService: AdaptCommonDialogService,
    ) {
        const saveLocationMode = LocalStorage.get<ChatLocationMode>(ChatLocationModeStorageKey) ?? ChatLocationMode.ApplicationBarDialog;
        this.chatLocationModeSubject = new BehaviorSubject<ChatLocationMode>(saveLocationMode);

        this.orgChangedEvent$ = this.chatAiOrgProvider.organisationChanged$.pipe(
            distinctUntilChanged(),
        );
        this.orgChangedEvent$.pipe(
            debounceTime(200),
        ).subscribe(() => {
            this.abortActiveResponseStream("Chat aborted because the organisation changed.");
            this.startNewChatGeneration();
            this.persistedChat = undefined;
        });

        this.featureEnabled$ = merge(
            this.chatAiOrgProvider.authorisationChanged$,
            this.orgChangedEvent$,
        ).pipe(
            debounceTime(100), // in case multiple events are triggered together, we only need to check once
            startWith(undefined),
            switchMap(() => this.chatAiOrgProvider.promiseToCheckIfFeatureActive(FeatureName.PlatformAI)),
        );

        this.applicableMegaPromptLabels$ = this.chatAiOrgProvider.applicableMegaPromptLabels$;
    }

    public get chatVisibleChange$() {
        return this.chatVisibleChangeEvent$.asObservable();
    }

    public get tabClearedEvent$() {
        return this.persistedChatClearedEvent$.asObservable();
    }

    public get responseStreaming$() {
        return this.responseStreamingSubject.asObservable();
    }

    public get isResponseStreaming() {
        return this.responseStreamingSubject.value;
    }

    public get currentChatGeneration() {
        return this.chatGeneration;
    }

    public get isSwitchingLocationMode() {
        const onceOffSwitchingFlag = this.switchingLocationMode;
        if (onceOffSwitchingFlag) {
            this.switchingLocationMode = false;
        }

        return onceOffSwitchingFlag;
    }

    public get chatLocationMode() {
        return this.hasChatAiSidebarTab ? this.chatLocationModeSubject.value : ChatLocationMode.ApplicationBarDialog;
    }

    public get chatLocationMode$() {
        return this.chatLocationModeSubject.asObservable().pipe(
            map((mode) => this.hasChatAiSidebarTab ? mode : ChatLocationMode.ApplicationBarDialog),
        );
    }

    public set chatLocationMode(mode: ChatLocationMode) {
        this.switchingLocationMode = mode !== this.chatLocationMode;
        this.chatLocationModeSubject.next(mode);
        LocalStorage.set(ChatLocationModeStorageKey, mode);
    }

    public queueMegaPromptExecution(megaPromptLabel: IMegaPromptLabel) {
        this.queueExecutePromptName$.next(megaPromptLabel);
    }

    public clearMegaPromptExecutionQueue() {
        this.queueExecutePromptName$.next(undefined);
    }

    public get queuedMegaPromptExecution$(): Observable<IMegaPromptLabel> {
        return this.queueExecutePromptName$.asObservable().pipe(
            filter((label): label is IMegaPromptLabel => !!label),
        );
    }

    public isFeatureEnabledForCurrentBreakpoint$() {
        return combineLatest([
            this.featureEnabled$,
            this.responsiveService.currentBreakpoint$,
        ]).pipe(
            map(([isFeatureActive, breakpoint]) => isFeatureActive && breakpoint.isDesktopSize),
        );
    }

    public isFeatureEnabledForLocationMode$(chatLocationMode: ChatLocationMode) {
        return combineLatest([
            this.isFeatureEnabledForCurrentBreakpoint$(),
            this.chatLocationMode$.pipe(map((mode) => mode === chatLocationMode)),
        ]).pipe(
            map(([isFeatureEnabledForCurrentBreakpoint, isMatchingLocationMode]) => isFeatureEnabledForCurrentBreakpoint && isMatchingLocationMode),
        );
    }

    public triggerChatDialog() {
        this.switchingLocationMode = true;
        // re-emit the current location mode to trigger the dialog open logic in the component that listens to it. This is needed for the case when the user has already switched to the location mode but the dialog is closed, so clicking the sidebar button will open it again.
        this.chatLocationModeSubject.next(this.chatLocationMode);
    }

    public openSidebar() {
        this.shellUiService.focusTab(ChatAiSidebarTabId);
    }

    public persistChat(chat: IChat, totalCost: number) {
        this.persistedChat = {
            chat,
            totalCost,
        };
    }

    public getPersistedChat() {
        return this.persistedChat;
    }

    public startNewChatGeneration() {
        this.chatGeneration++;
    }

    public async streamResponse(body: IChatResponseBindingModel, processResponse: (response: Response) => Promise<void>) {
        if (this.activeResponsePromise) {
            throw new Error("An AI response is already being streamed.");
        }

        const abortController = new AbortController();
        this.responseAbortController = abortController;
        this.setResponseStreaming(true);

        const responsePromise = this.fetchAndProcessResponse(body, abortController, processResponse);
        this.activeResponsePromise = responsePromise;
        try {
            await responsePromise;
        } finally {
            this.responseAbortController = undefined;
            this.activeResponsePromise = undefined;
            // Let the caller finish applying the final chat state before live/reopened views render the stream as complete.
            setTimeout(() => {
                if (!this.activeResponsePromise) {
                    this.setResponseStreaming(false);
                }
            });
        }
    }

    public abortActiveResponseStream(reason: string) {
        if (!this.responseAbortController || this.responseAbortController.signal.aborted) {
            return;
        }

        this.responseAbortController.abort(reason);
    }

    public async waitForActiveResponseToFinish(timeoutMs = 3000) {
        const activeResponsePromise = this.activeResponsePromise;
        if (!activeResponsePromise) {
            return;
        }

        await Promise.race([
            activeResponsePromise.catch(() => undefined),
            new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
        ]);
    }

    public emitChatVisibleChange() {
        this.chatVisibleChangeEvent$.next();
    }

    private setResponseStreaming(isStreaming: boolean) {
        if (this.responseStreamingSubject.value !== isStreaming) {
            this.responseStreamingSubject.next(isStreaming);
        }
    }

    private async fetchAndProcessResponse(
        body: IChatResponseBindingModel,
        abortController: AbortController,
        processResponse: (response: Response) => Promise<void>,
    ) {
        let streamTimeoutId: ReturnType<typeof setTimeout> | undefined;
        try {
            const response = await fetch(`${ServiceUri.AIChatServiceUri}/Responses`, {
                method: "POST",
                body: JSON.stringify(body),
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${this.identityStorageService.accessToken!}`,
                },
                signal: abortController.signal,
            });

            if (!response.ok) {
                throw new HttpErrorResponse({
                    status: response.status,
                    statusText: response.statusText,
                    url: response.url,
                });
            }

            streamTimeoutId = setTimeout(
                () => this.abortActiveResponseStream("The AI response stream timed out after 10 minutes"),
                StreamTimeoutMs,
            );
            await processResponse(response);
        } catch (error) {
            if (abortController.signal.aborted) {
                throw new ChatResponseAbortedError(
                    typeof abortController.signal.reason === "string"
                        ? abortController.signal.reason
                        : "AbortError: The request was aborted without a reason.",
                );
            }

            throw error;
        } finally {
            clearTimeout(streamTimeoutId);
        }
    }

    public uploadFile(file: File, backendAttachmentName: string): Observable<HttpEvent<string>> {
        const formData = new FormData();
        formData.append("OrganisationId", this.chatAiOrgProvider.getOrganisationId().toString());
        formData.append("File", file, backendAttachmentName);

        const url = `${ServiceUri.AIChatServiceUri}/UploadFile`;

        return this.httpClient.post(url, formData, {
            headers: {
                Authorization: `Bearer ${this.identityStorageService.accessToken!}`,
            },
            observe: "events",
            reportProgress: true,
            responseType: "text",
        });
    }

    public deleteAttachmentFile(fileId: string): Observable<HttpResponse<string>> {
        const query = new URLSearchParams({
            organisationId: this.chatAiOrgProvider.getOrganisationId().toString(),
            fileId,
        });

        return this.httpClient.delete(`${ServiceUri.AIChatServiceUri}/DeleteFile?${query.toString()}`, {
            headers: {
                Authorization: `Bearer ${this.identityStorageService.accessToken!}`,
            },
            observe: "response",
            responseType: "text",
        });
    }

    public downloadAttachmentFile(fileId: string): Observable<HttpResponse<Blob>> {
        const query = new URLSearchParams({
            organisationId: this.chatAiOrgProvider.getOrganisationId().toString(),
            fileId,
        });

        return this.httpClient.get(`${ServiceUri.AIChatServiceUri}/DownloadFile?${query.toString()}`, {
            headers: {
                Authorization: `Bearer ${this.identityStorageService.accessToken!}`,
            },
            observe: "response",
            responseType: "blob",
        });
    }

    public getMegaPromptNames$(fromNimbus = false, forceRemote = false): Observable<MegaPromptName[]> {
        if (forceRemote || !this.cachedMegaPromptNames$) {
            this.cachedMegaPromptNames$ = this.httpClient.get<MegaPromptName[]>(
                `${ServiceUri.AIChatServiceUri}/MegaPromptNames`,
                fromNimbus ? { params: { fromNimbus: true } } : undefined,
            ).pipe(
                shareReplay(1),
            );
        }
        return this.cachedMegaPromptNames$;
    }

    public getConversations(updatedSince?: Date, forceRemote = false): Observable<ChatAudit[]> {
        // The server can return deleted entities for client to update if required
        // - so client can remove from local entity manager
        const predicate = this.commonConversationPredicate();
        return this.commonDataService.getWithOptions(ChatAuditBreezeModel, "conversations", {
            predicate,
            namedParams: {
                organisationId: this.chatAiOrgProvider.getOrganisationId(),
                personId: this.userService.getCurrentPersonId(),
                personalData: true,
                updatedSince: updatedSince?.toISOString(),
            },
            orderBy: "endTime DESC",
            forceRemote,
        }).pipe(
            switchMap((conversations) => {
                const deletedConversations = conversations.filter((c) => c.deleted);
                // detach instead of delete as we still want the data for audit
                deletedConversations.forEach((conversation) => this.commonDataService.detachEntityFromBreeze(conversation));
                if (updatedSince) {
                    // this will be a cached call without hitting the server to return results integrating updated since
                    return this.getConversations(undefined, false);
                } else {
                    return of(conversations.filter((c) => !c.deleted)
                        // sort by end time desc as it will not be sorted if forceRemote in the optimization Steve did
                        .sort((a, b) => b.endTime.getTime() - a.endTime.getTime()));
                }
            }),
        );
    }

    public getLatestConversation(forceRemote = false) {
        // only get undeleted conversation only for latest
        const predicate = new MethodologyPredicate<ChatAudit>("deleted", "!=", true)
            .and(this.commonConversationPredicate());
        return this.commonDataService.getWithOptions(ChatAuditBreezeModel, "conversations", {
            predicate,
            namedParams: {
                organisationId: this.chatAiOrgProvider.getOrganisationId(),
                personId: this.userService.getCurrentPersonId(),
                personalData: true,
            },
            orderBy: "endTime DESC",
            top: 1, // this is OK - forceRemote with top will be sorted by the server
            forceRemote,
        }).pipe(
            map(ArrayUtilities.getSingleFromArray),
        );
    }

    public deleteConversation(conversationId: string): Observable<HttpResponse<string>> {
        const query = new URLSearchParams({ conversationId });
        return this.httpClient.delete(`${ServiceUri.AIChatServiceUri}/DeleteConversation?${query.toString()}`, {
            observe: "response",
            responseType: "text",
        });
    }

    public renameConversation(chatAudit: ChatAudit) {
        const entities: IEditSimpleEntity[] = [{
            entity: chatAudit,
            fieldName: "subject",
            label: "Conversation name",
            type: SimpleValueType.Text,
            usePropertyValidator: true,
        }];

        return this.dialogService.open(EditSimpleValueBreezeEntityDialogComponent, {
            title: "Rename conversation",
            entities,
            dialogWidth: 480,
            saveOnClose: true,
        } as IEditSimpleValueBreezeEntityDialogData);
    }

    public getConversationHistory(conversationId: string): Observable<IChatHistoryEntry[]> {
        const query = new URLSearchParams({ conversationId });
        return this.httpClient.get<IChatHistoryEntry[]>(
            `${ServiceUri.AIChatServiceUri}/GetChatHistory?${query.toString()}`).pipe(
                map((history) => history.map((entry) => ({
                    ...entry,
                    timestamp: new Date(entry.timestamp),
                    userMessageTimestamp: new Date(entry.userMessageTimestamp),
                })).sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())),
            );
    }

    // still need this predicate even if the personId is already processed from server as:
    // - nimbus will get others person's conversations and we don't want to show those in this list
    // - nimbus will also get Tools cost (with a lesser model) which is not required when looking for conversation history
    // For nimbus, organisationId will be null even for conversation history
    private commonConversationPredicate = () => new MethodologyPredicate<ChatAudit>("personId", "==", this.userService.getCurrentPersonId())
        .and(new MethodologyPredicate<ChatAudit>("requestType", "==", RequestType.ChatStreaming))
        .and(new MethodologyPredicate<ChatAudit>("organisationId", "==", this.chatAiOrgProvider.getOrganisationId() || null));
}
